/**
 * API 工具函数
 * 支持 OpenAI 兼容的 SSE 流式调用
 */

// 使用环境变量配置 API 地址
const API_BASE_URL = process.env.REACT_APP_API_URL || '';
const API_TOKEN = '1';

// =============================================================================
// 类型定义
// =============================================================================

export interface ProgressInfo {
  stage: string;
  status: string;
  message: string;
  percent?: number;
}

export interface ChatMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

export interface ChatCompletionRequest {
  model: string;
  messages: ChatMessage[];
  stream?: boolean;
  temperature?: number;
  max_tokens?: number;
}

export interface ChatCompletionChunk {
  id: string;
  object: string;
  created: number;
  model: string;
  choices: Array<{
    index: number;
    delta: {
      role?: string;
      content?: string;
    };
    finish_reason: string | null;
  }>;
  // 扩展字段（支持两种命名方式）
  _progress?: ProgressInfo;
  _stage_content?: string;
  progress?: ProgressInfo;
  stage_content?: string;
  task_id?: string;
  reports?: {
    no_reference?: string;
    with_reference?: string;
  };
}

export interface GenerateResponse {
  task_id?: string;
  status?: string;
  success?: boolean;
  error?: string;
  output_files?: string[];
  task_dir?: string;
  reports?: {
    no_reference?: string;
    with_reference?: string;
    no_reference_pdf?: string;
    with_reference_pdf?: string;
  };
}

export interface ReportContent {
  reports: {
    no_reference?: string;
    with_reference?: string;
  };
}

// =============================================================================
// OpenAI 兼容的流式 API
// =============================================================================

export interface StreamOptions {
  onContent: (content: string) => void;
  onProgress?: (progress: ProgressInfo) => void;
  onStageContent?: (stage: string, content: string) => void;
  onComplete?: (result: StreamResult) => void;
  onError?: (error: string) => void;
}

export interface StreamResult {
  taskId: string;
  reports: {
    no_reference?: string;
    with_reference?: string;
  };
}

/**
 * OpenAI 兼容的流式聊天完成接口
 *
 * @param request - 聊天请求
 * @param options - 流式回调选项
 * @returns 取消函数
 */
export const streamChatCompletions = (
  request: ChatCompletionRequest,
  options: StreamOptions
): (() => void) => {
  const controller = new AbortController();

  // 添加完成标志，防止 onComplete 被多次调用
  let completed = false;
  // 保存最后一个有效的 chunk 数据，防止 [DONE] 先到达
  let lastValidChunk: ChatCompletionChunk | null = null;

  const executeStream = async () => {
    const url = `${API_BASE_URL}/v1/chat/completions`;
    const payload = {
      ...request,
      stream: true,
    };

    console.log('[前端SSE] ========== 开始请求 ==========');
    console.log('[前端SSE] API_BASE_URL:', API_BASE_URL);
    console.log('[前端SSE] 完整 URL:', url);
    console.log('[前端SSE] 请求内容:', JSON.stringify(payload, null, 2));

    try {
      console.log('[前端SSE] 发起 fetch 请求...');
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${API_TOKEN}`,
        },
        body: JSON.stringify({
          ...request,
          stream: true,
        }),
        signal: controller.signal,
      });

      console.log('[前端SSE] 收到响应:', response.status, response.statusText);
      console.log('[前端SSE] 响应头:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        console.error('[前端SSE] 响应错误:', response.status, response.statusText);
        throw new Error(`API 请求失败: ${response.status} ${response.statusText}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        console.error('[前端SSE] 无法获取响应流');
        throw new Error('无法获取响应流');
      }

      console.log('[前端SSE] 开始读取流...');

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          console.log('[前端SSE] 流读取完成');
          break;
        }

        const chunk = decoder.decode(value, { stream: true });
        console.log('[前端SSE] 收到数据块:', chunk.length, '字节');
        buffer += chunk;

        // 处理 SSE 格式: data: {...}\n\n
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || ''; // 保留不完整的行

        console.log('[前端SSE] 解析出', lines.length, '行 SSE 数据');

        for (const line of lines) {
          if (!line.trim() || !line.startsWith('data: ')) continue;

          const data = line.slice(6).trim();
          console.log('[前端SSE] SSE data:', data.substring(0, 100) + (data.length > 100 ? '...' : ''));

          // 检查结束标记
          if (data === '[DONE]') {
            if (options.onComplete && !completed) {
              completed = true;
              // 使用最后保存的有效 chunk 数据
              options.onComplete({
                taskId: lastValidChunk?.task_id || '',
                reports: lastValidChunk?.reports || {},
              });
            }
            return;
          }

          try {
            const chunk: ChatCompletionChunk = JSON.parse(data);

            // 保存包含 task_id 或 reports 的有效 chunk
            if (chunk.task_id || chunk.reports) {
              lastValidChunk = chunk;
            }

            // 处理进度信息（支持两种字段名）
            const progressInfo = chunk._progress || chunk.progress;
            if (progressInfo && options.onProgress) {
              options.onProgress(progressInfo);
            }

            // 处理阶段内容（支持两种字段名）
            const stageContent = chunk._stage_content || chunk.stage_content;
            if (stageContent && options.onStageContent) {
              options.onStageContent(progressInfo?.stage || '', stageContent);
            }

            // 处理内容
            if (chunk.choices?.[0]?.delta?.content) {
              const content = chunk.choices[0].delta.content;
              options.onContent(content);
            }

            // 处理完成
            if (chunk.choices?.[0]?.finish_reason === 'stop') {
              if (options.onComplete && !completed) {
                completed = true;
                options.onComplete({
                  taskId: chunk.task_id || '',
                  reports: chunk.reports || {},
                });
              }
            }
          } catch (e) {
            console.error('解析 SSE 数据失败:', e, data);
          }
        }
      }
      console.log('[前端SSE] ========== 请求处理完成 ==========');
    } catch (error) {
      console.error('[前端SSE] ========== 请求异常 ==========');
      console.error('[前端SSE] 错误类型:', error?.constructor?.name);
      console.error('[前端SSE] 错误信息:', error);
      console.error('[前端SSE] 错误堆栈:', (error as Error)?.stack);

      if (error instanceof Error && error.name === 'AbortError') {
        // 用户主动取消
        console.log('[前端SSE] 用户主动取消');
        return;
      }
      console.error('[前端SSE] 流式请求失败:', error);
      if (options.onError) {
        options.onError(error instanceof Error ? error.message : '请求失败');
      }
    }
  };

  console.log('[前端SSE] 设置执行，准备调用 executeStream...');
  executeStream();

  // 返回取消函数
  return () => controller.abort();
};

// =============================================================================
// 原有 API 接口（保持兼容）
// =============================================================================

/**
 * 生成报告（同步接口）
 */
export const generateReport = async (query: string): Promise<GenerateResponse> => {
  const response = await fetch(`${API_BASE_URL}/api/generate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ query }),
  });

  if (!response.ok) {
    throw new Error(`生成报告失败: ${response.statusText}`);
  }

  return response.json();
};

/**
 * 获取任务状态
 */
export const getTaskStatus = async (taskId: string): Promise<any> => {
  const response = await fetch(`${API_BASE_URL}/api/task/${taskId}`);

  if (!response.ok) {
    throw new Error(`获取任务状态失败: ${response.statusText}`);
  }

  return response.json();
};

/**
 * 获取报告内容
 */
export const getReportContent = async (taskId: string): Promise<ReportContent> => {
  const response = await fetch(`${API_BASE_URL}/api/task/${taskId}/report`);

  if (!response.ok) {
    throw new Error(`获取报告内容失败: ${response.statusText}`);
  }

  return response.json();
};

/**
 * 下载报告
 *
 * @param taskId - 任务 ID
 * @param reportType - 报告类型 (no_reference, with_reference)
 * @param format - 文件格式 (md, pdf)
 */
export const downloadReport = (
  taskId: string,
  reportType: 'no_reference' | 'with_reference' = 'no_reference',
  format: 'md' | 'pdf' = 'md'
): void => {
  // PDF 格式使用前端生成（避免 WeasyPrint 的 GTK3 依赖问题）
  if (format === 'pdf') {
    downloadReportAsPDF(taskId, reportType);
    return;
  }

  // Markdown 格式直接下载
  fetch(`${API_BASE_URL}/api/download/${taskId}?report_type=${reportType}&format=md`)
    .then(response => {
      if (!response.ok) {
        throw new Error('下载失败');
      }

      // 从 Content-Disposition 头中获取文件名
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `report_${taskId}.md`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename\*?=UTF-8''(.+)|filename="?([^"]+)"?/i);
        if (filenameMatch) {
          filename = decodeURIComponent(filenameMatch[1] || filenameMatch[2]);
        }
      }

      return response.blob().then(blob => ({ blob, filename }));
    })
    .then(({ blob, filename }) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    })
    .catch(error => {
      console.error('下载报告失败:', error);
      alert('下载报告失败，请重试');
    });
};

/**
 * 使用浏览器打印功能生成 PDF
 */
const downloadReportAsPDF = (
  taskId: string,
  reportType: 'no_reference' | 'with_reference' = 'no_reference'
): void => {
  console.log('[PDF下载] 使用前端生成 PDF');

  // 先获取 Markdown 内容
  fetch(`${API_BASE_URL}/api/download/${taskId}?report_type=${reportType}&format=md`)
    .then(response => {
      if (!response.ok) {
        throw new Error('获取报告内容失败');
      }
      return response.text();
    })
    .then(markdownContent => {
      // 创建打印窗口
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        alert('无法打开打印窗口，请允许弹出窗口');
        return;
      }

      // 简单的 Markdown 转 HTML
      const htmlContent = convertMarkdownToHTML(markdownContent);

      // 写入打印页面
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>报告</title>
          <style>
            body {
              font-family: "Microsoft YaHei", "SimHei", Arial, sans-serif;
              line-height: 1.6;
              max-width: 800px;
              margin: 0 auto;
              padding: 20px;
              color: #333;
            }
            h1, h2, h3, h4, h5, h6 {
              color: #333;
              margin-top: 20px;
              margin-bottom: 10px;
              page-break-after: avoid;
            }
            h1 { font-size: 24px; border-bottom: 2px solid #eee; padding-bottom: 10px; }
            h2 { font-size: 20px; border-bottom: 1px solid #eee; padding-bottom: 8px; }
            h3 { font-size: 18px; }
            h4 { font-size: 16px; }
            p { margin: 10px 0; }
            table {
              border-collapse: collapse;
              width: 100%;
              margin: 20px 0;
              page-break-inside: avoid;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: left;
            }
            th {
              background-color: #f2f2f2;
            }
            code {
              background-color: #f4f4f4;
              padding: 2px 4px;
              border-radius: 3px;
              font-family: monospace;
            }
            pre {
              background-color: #f4f4f4;
              padding: 10px;
              border-radius: 5px;
              overflow-x: auto;
              page-break-inside: avoid;
            }
            a {
              color: #0066cc;
              text-decoration: none;
            }
            @media print {
              body { width: 100%; margin: 0; padding: 10px; }
              h1 { page-break-before: auto; }
            }
          </style>
        </head>
        <body>
          ${htmlContent}
        </body>
        </html>
      `);
      printWindow.document.close();

      // 等待页面加载完成后弹出打印对话框
      setTimeout(() => {
        printWindow.print();
      }, 500);
    })
    .catch(error => {
      console.error('[PDF下载] 失败:', error);
      alert('PDF 生成失败，请重试或下载 Markdown 格式');
    });
};

/**
 * 简单的 Markdown 转 HTML
 */
const convertMarkdownToHTML = (markdown: string): string => {
  let html = markdown;

  // 标题
  html = html.replace(/^### (.*$)/gim, '<h3>$1</h3>');
  html = html.replace(/^## (.*$)/gim, '<h2>$1</h2>');
  html = html.replace(/^# (.*$)/gim, '<h1>$1</h1>');

  // 粗体
  html = html.replace(/\*\*(.*?)\*\*/gim, '<strong>$1</strong>');

  // 斜体
  html = html.replace(/\*(.*?)\*/gim, '<em>$1</em>');

  // 代码块
  html = html.replace(/```([\s\S]*?)```/gim, '<pre><code>$1</code></pre>');

  // 行内代码
  html = html.replace(/`([^`]+)`/gim, '<code>$1</code>');

  // 链接
  html = html.replace(/\[([^\]]+)\]\(([^\)]+)\)/gim, '<a href="$2">$1</a>');

  // 表格（简单处理）
  const lines = html.split('\n');
  let inTable = false;
  let tableRows: string[] = [];
  let result: string[] = [];

  for (const line of lines) {
    if (line.startsWith('|') && line.endsWith('|')) {
      if (!inTable) {
        inTable = true;
        tableRows = [];
      }
      // 跳过分隔行
      if (!line.match(/^\|[\s\-:]+\|[\s\-:]+/)) {
        tableRows.push(line);
      }
    } else {
      if (inTable) {
        result.push(convertTableToHTML(tableRows));
        tableRows = [];
        inTable = false;
      }
      result.push(line);
    }
  }
  if (inTable && tableRows.length > 0) {
    result.push(convertTableToHTML(tableRows));
  }

  html = result.join('\n');

  // 段落
  html = html.replace(/\n\n/g, '</p><p>');
  html = '<p>' + html + '</p>';

  // 清理空段落
  html = html.replace(/<p>\s*<\/p>/g, '');
  html = html.replace(/<p>(<h[1-6]>)/g, '$1');
  html = html.replace(/(<\/h[1-6]>)<\/p>/g, '$1');
  html = html.replace(/<p>(<pre>)/g, '$1');
  html = html.replace(/(<\/pre>)<\/p>/g, '$1');
  html = html.replace(/<p>(<\/table>)/g, '$1');

  return html;
};

/**
 * 将 Markdown 表格行转换为 HTML 表格
 */
const convertTableToHTML = (rows: string[]): string => {
  if (rows.length === 0) return '';

  let html = '<table>';

  for (let i = 0; i < rows.length; i++) {
    const cells = rows[i].split('|').slice(1, -1).map(c => c.trim());
    const tag = i === 0 ? 'th' : 'td';
    html += '<tr>';
    for (const cell of cells) {
      html += `<${tag}>${cell}</${tag}>`;
    }
    html += '</tr>';
  }

  html += '</table>';
  return html;
};

/**
 * 获取报告下载信息
 */
export const getReportInfo = async (taskId: string): Promise<any> => {
  const response = await fetch(`${API_BASE_URL}/api/download/${taskId}/info`);

  if (!response.ok) {
    throw new Error(`获取报告信息失败: ${response.statusText}`);
  }

  return response.json();
};

/**
 * 获取历史记录
 */
export const getHistory = async (): Promise<{ history: any[] }> => {
  const response = await fetch(`${API_BASE_URL}/api/history`);

  if (!response.ok) {
    throw new Error('获取历史记录失败');
  }

  return response.json();
};

/**
 * 保存历史记录
 */
export const saveHistoryEntry = async (entry: {
  query: string;
  task_id: string;
  report_type?: string;
  region?: string;
  target_date?: string;
}): Promise<{ success: boolean }> => {
  const response = await fetch(`${API_BASE_URL}/api/history`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(entry),
  });

  if (!response.ok) {
    throw new Error('保存历史记录失败');
  }

  return response.json();
};

/**
 * 健康检查
 */
export const healthCheck = async (): Promise<{ status: string; service: string }> => {
  const response = await fetch(`${API_BASE_URL}/health`);

  if (!response.ok) {
    throw new Error('服务不可用');
  }

  return response.json();
};

// =============================================================================
// 便捷方法
// =============================================================================

/**
 * 便捷方法：使用流式接口生成报告
 *
 * @param query - 用户查询
 * @param options - 流式回调选项
 * @returns 取消函数
 */
export const streamGenerateReport = (
  query: string,
  options: StreamOptions
): (() => void) => {
  return streamChatCompletions(
    {
      model: 'financial-report-generator',
      messages: [{ role: 'user', content: query }],
      stream: true,
    },
    options
  );
};
